import Constants from "Components/MessageToaster/Constants";
import { CallHistoryMethodAction, push, replace } from "connected-react-router";
import { ITaskTemplateFilterFactory } from "Factories/Tasks/ITaskTemplateFilterFactory";
import { ITaskTemplateController } from "Interfaces/Controllers/ITaskTemplateController";
import { inject, injectable } from "inversify";
import { DataState } from "Models/Common/DataState";
import { TemplateFilter } from "Models/Filters/TemplateFilter";
import { TaskTemplate } from "Models/Tasks/TaskTemplate";
import { Dispatch } from "react";
import Types from "ServiceProvider/Types";
import i18n from "Translations/i18n";
import { ActionTypes as MessageActionTypes, IShowMessageToasterAction } from "../MessageToaster/MessageToasterActionTypes";
import { ActionTypes, ITaskTemplateTagsRetrievedAction, TaskTemplateActions } from "./TaskTemplateActionTypes";
import { TaskTemplateFilterState } from "./TaskTemplateFilterState";

export interface ITaskTemplateAction {
    clearTaskTemplatesAsync(): any;
    getTaskTemplatesByFilterAsync(filter: TemplateFilter, shouldClearCurrentResult?: boolean): any;
    getTaskTemplateByIdAsync(taskTemplateId: number): any;
    saveTaskTemplateAsync(taskTemplate: TaskTemplate, pictureFile?: File, descriptionFile?: File, videoFile?: File, videoThumbnailFile?: File, addAnother?: boolean): any;
    updatePositionIndexAsync(taskTemplates: TaskTemplate[]): any;
    deleteTaskTemplateByIdAsync(taskTemplateId: number): any;
    clearValidationErrorsByIdAsync(taskTemplateId?: number): any;
    clearArrangeTaskTemplatesAsync(): any;
    getArrangeTaskTemplatesAsync(): any;
    getTaskTemplateTagsForCompanyAsync(): any;
    updateArrangeFilterAsync(filter: TemplateFilter): any;
}

@injectable()
export class TaskTemplateAction implements ITaskTemplateAction {
    private taskTemplateController: ITaskTemplateController;
    private taskTemplateFilterState: ITaskTemplateFilterFactory;

    public constructor(
        @inject(Types.ITaskTemplateController) taskTemplateController: ITaskTemplateController,
        @inject(Types.ITaskTemplateFilterFactory) taskTemplateFilterState: ITaskTemplateFilterFactory) {
        this.taskTemplateController = taskTemplateController;
        this.taskTemplateFilterState = taskTemplateFilterState;
    }

    public getTaskTemplatesByFilterAsync(filterState: TaskTemplateFilterState, shouldClearCurrentResult: boolean = false) {
        return async (dispatch: Dispatch<TaskTemplateActions | IShowMessageToasterAction>) => {
            if (shouldClearCurrentResult) {
                dispatch({
                    type: ActionTypes.CLEAR_TEMPLATES
                });
            }

            dispatch({
                type: ActionTypes.FILTER_UPDATED,
                filter: filterState
            });

            try {
                const filter = this.taskTemplateFilterState.create(filterState);
                const taskTemplatePage = await this.taskTemplateController.getByFilterAsync(filter);

                dispatch({
                    type: ActionTypes.PAGE_RETRIEVED,
                    page: taskTemplatePage
                });
            }
            catch (ex) {
                dispatch({
                    type: MessageActionTypes.SHOW_MESSAGE,
                    data: { type: Constants.MESSAGE_TYPE_DANGER, text: i18n.t("tasks.errors.retrieving") }
                });
            }
        };
    }

    public updateArrangeFilterAsync(filter: TemplateFilter) {
        return async (dispatch: Dispatch<TaskTemplateActions>) => {
            dispatch({
                type: ActionTypes.ARRANGE_FILTER_UPDATED,
                filter
            });
        };
    }

    public clearTaskTemplatesAsync() {
        return async (dispatch: Dispatch<TaskTemplateActions>) => {
            dispatch({
                type: ActionTypes.CLEAR_TEMPLATES
            });
        };
    }

    public clearArrangeTaskTemplatesAsync() {
        return async (dispatch: Dispatch<TaskTemplateActions>) => {
            dispatch({
                type: ActionTypes.ARRANGE_CLEAR_TASK_TEMPLATES
            });
        };
    }

    public getArrangeTaskTemplatesAsync() {
        return async (dispatch: Dispatch<TaskTemplateActions | IShowMessageToasterAction>) => {
            dispatch({
                type: ActionTypes.ARRANGE_RETRIEVING_TASK_TEMPLATES
            });

            try {
                const arrangeTaskTemplates = await this.taskTemplateController.getArrangeTaskTemplatesAsync();

                dispatch({
                    type: ActionTypes.ARRANGE_RETRIEVED_TASK_TEMPLATES,
                    arrangeTaskTemplates
                });
            }
            catch (ex) {
                dispatch({
                    type: MessageActionTypes.SHOW_MESSAGE,
                    data: { type: Constants.MESSAGE_TYPE_DANGER, text: i18n.t("tasks.errors.retrieving") }
                });
            }
        };
    }

    public getTaskTemplateTagsForCompanyAsync() {
        return async (dispatch: Dispatch<ITaskTemplateTagsRetrievedAction | IShowMessageToasterAction>) => {
            try {
                const taskTemplateTags = await this.taskTemplateController.getTaskTemplateTagsForCompanyAsync();

                dispatch({
                    type: ActionTypes.TASK_TEMPLATE_TAGS_RETRIEVED,
                    data: taskTemplateTags
                });
            }
            catch (ex) {
                dispatch({
                    type: MessageActionTypes.SHOW_MESSAGE,
                    data: { type: Constants.MESSAGE_TYPE_DANGER, text: i18n.t("tasks.tags.errors.retrieving") }
                });
            }
        };
    }

    public getTaskTemplateByIdAsync(taskTemplateId: number) {
        return async (dispatch: Dispatch<TaskTemplateActions | IShowMessageToasterAction>) => {
            dispatch({
                type: ActionTypes.TASK_TEMPLATE_STATE_CHANGED,
                taskTemplateId,
                newState: DataState.Loading
            });

            try {
                const taskTemplate = await this.taskTemplateController.getByIdAsync(taskTemplateId);

                dispatch({
                    type: ActionTypes.TASK_TEMPLATE_RETRIEVED,
                    taskTemplate
                });
            }
            catch (ex) {
                dispatch({
                    type: MessageActionTypes.SHOW_MESSAGE,
                    data: { type: Constants.MESSAGE_TYPE_DANGER, text: i18n.t("tasks.errors.retrieving") }
                });
            }
        };
    }

    public deleteTaskTemplateByIdAsync(taskTemplateId: number) {
        return async (dispatch: Dispatch<TaskTemplateActions | IShowMessageToasterAction>) => {
            dispatch({
                type: ActionTypes.TASK_TEMPLATE_STATE_CHANGED,
                taskTemplateId,
                newState: DataState.Deleting
            });

            try {
                await this.taskTemplateController.deleteByIdAsync(taskTemplateId);

                dispatch({
                    type: ActionTypes.TASK_TEMPLATE_DELETED,
                    taskTemplateId
                });

                dispatch({
                    type: MessageActionTypes.SHOW_MESSAGE,
                    data: { type: Constants.MESSAGE_TYPE_SUCCESS, text: i18n.t("tasks.success.deleted") }
                });
            }
            catch (ex) {
                dispatch({
                    type: MessageActionTypes.SHOW_MESSAGE,
                    data: { type: Constants.MESSAGE_TYPE_DANGER, text: i18n.t("tasks.errors.deleting") }
                });
            }
        };
    }

    public clearValidationErrorsByIdAsync(taskTemplateId?: number) {
        return async (dispatch: Dispatch<TaskTemplateActions>) => {
            dispatch({
                type: ActionTypes.CLEAR_VALIDATION_ERRORS,
                taskTemplateId
            });
        };
    }

    public saveTaskTemplateAsync(taskTemplate: TaskTemplate, pictureFile?: File, descriptionFile?: File, videoFile?: File, videoThumbnailFile?: File, addAnother?: boolean) {
        return async (dispatch: Dispatch<TaskTemplateActions | IShowMessageToasterAction | CallHistoryMethodAction>) => {
            dispatch({
                type: ActionTypes.TASK_TEMPLATE_STATE_CHANGED,
                taskTemplateId: taskTemplate.id,
                newState: DataState.Saving
            });
            dispatch({
                type: ActionTypes.CLEAR_VALIDATION_ERRORS,
                taskTemplateId: taskTemplate.id
            });

            try {
                const templateUpdate: TaskTemplate = { ...taskTemplate, area: { ...taskTemplate.area, parent: undefined, children: undefined } };
                const isNewTemplate = !taskTemplate.id;

                let newTaskTemplate = await (isNewTemplate ?
                    this.taskTemplateController.insertOneAsync(templateUpdate) :
                    this.taskTemplateController.updateOneAsync(templateUpdate));

                if (videoFile || pictureFile || descriptionFile || videoThumbnailFile) {
                    newTaskTemplate = await this.taskTemplateController.uploadMediaAsync(newTaskTemplate, videoFile, videoThumbnailFile, pictureFile, descriptionFile);
                }

                for (const step of taskTemplate.steps) {
                    if (step.pictureFile || step.videoFile || step.videoThumbnailFile) {
                        let stepId = step.id;
                        let stepOfNewTaskTemplate = null;
                        if (!stepId || stepId < 0) {
                            const newStep = newTaskTemplate.steps.find(x => x.positionIndex === step.positionIndex);
                            if (newStep) {
                                stepId = newStep.id;
                                stepOfNewTaskTemplate = newStep;
                            }
                        }

                        if (stepId) {
                            step.id = stepId;
                            const newSavedStep = await this.taskTemplateController.uploadStepMediaAsync(newTaskTemplate, step);

                            if (!stepOfNewTaskTemplate) {
                                stepOfNewTaskTemplate = newTaskTemplate.steps.find(x => x.id === step.id);
                            }

                            // Set urls
                            stepOfNewTaskTemplate!.pictureUrl = newSavedStep.pictureUrl;
                            stepOfNewTaskTemplate!.videoUrl = newSavedStep.videoUrl;
                            stepOfNewTaskTemplate!.videoThumbnailUrl = newSavedStep.videoThumbnailUrl;
                        }
                    }
                }

                dispatch({
                    type: ActionTypes.TASK_TEMPLATE_SAVED,
                    taskTemplate: newTaskTemplate
                });
                dispatch({
                    type: MessageActionTypes.SHOW_MESSAGE,
                    data: { type: Constants.MESSAGE_TYPE_SUCCESS, text: i18n.t("tasks.success.saved") }
                });

                if (isNewTemplate) {
                    if (!addAnother) {
                        dispatch(push(`${newTaskTemplate.id}`));
                    }

                    // Set state of new TaskTemplate to done so it wont be saving immediately when adding a new TaskTemplate
                    dispatch({
                        type: ActionTypes.TASK_TEMPLATE_STATE_CHANGED,
                        newState: DataState.Done
                    });
                }

                if (addAnother) {
                    dispatch(push("/"));
                    dispatch(replace("/tasktemplates/new"));
                }
            }
            catch (ex) {
                if (ex.validationErrors) {
                    dispatch({
                        type: ActionTypes.SHOW_VALIDATION_ERRORS,
                        validationErrors: ex.validationErrors,
                        taskTemplateId: taskTemplate.id
                    });
                }
                else {
                    dispatch({
                        type: MessageActionTypes.SHOW_MESSAGE,
                        data: { 
                            type: Constants.MESSAGE_TYPE_DANGER, 
                            text: i18n.t(ex.response && ex.response.body ? `tasks.errors.${ex.response.body}` : "tasks.errors.saving") 
                        }
                    });
                }

                dispatch({
                    type: ActionTypes.TASK_TEMPLATE_STATE_CHANGED,
                    taskTemplateId: taskTemplate.id,
                    newState: DataState.Done
                });
            }
        };
    }

    public updatePositionIndexAsync(taskTemplates: TaskTemplate[]) {
        return async (dispatch: Dispatch<TaskTemplateActions | IShowMessageToasterAction | CallHistoryMethodAction>) => {

            try {
                await this.taskTemplateController.updatePositionIndexAsync(taskTemplates);
            }
            catch (ex) {
                dispatch({
                    type: MessageActionTypes.SHOW_MESSAGE,
                    data: { type: Constants.MESSAGE_TYPE_DANGER, text: i18n.t("tasks.errors.saving") }
                });
            }
        };
    }
}


// WEBPACK FOOTER //
// ./src/Store/TaskTemplates/TaskTemplateAction.ts